import { useEffect, useState } from 'react';
import { Col, Form, Row } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { PATTERN } from '../../../config/commons';
import { Store } from '../../../state/store.interfaces';
import Icon from '../../../common/components/Icon/Icon';
import { checkEmail } from '../../../utils/validations';
import { getUserSession } from '../../../utils/userSession';
import { UserSession } from '../../../common/interfaces/auth';
import { LrvForm } from '../../../common/components/LrvForm/LrvForm';
import { LrvText } from '../../../common/components/LrvText/LrvText';
import { useFormErrorFields } from '../../../hooks/useFormErrorFields';
import { LrvModal } from '../../../common/components/LrvModal/LrvModal';
import ActionButton from '../../../common/components/buttons/ActionButton';
import { LrvInputV2 } from '../../../common/components/LrvInputV2/LrvInputV2';

import * as mfaSlice from './mfaSlice';
import styles from './AuthenticatorAppForm.module.scss';

interface Props {
  theme?: 'dark' | 'light';
}

export const AuthenticatorEmailForm = (props: Props) => {
  const { theme } = props;

  const [t] = useTranslation();
  const [form] = Form.useForm();
  const { errorFields, resetErrorFields, extractAndSetErrorFields } = useFormErrorFields({ form });

  const dispatch = useDispatch();

  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');

  const {
    showCreateAuthenticatorEmailModal,
    isUpdating,
    showAuthenticatorEmailLabel,
    isSendingEmail,
  } = useSelector((state: Store) => state.mfa);

  const userSession: UserSession = getUserSession();

  useEffect(() => {
    if (!showCreateAuthenticatorEmailModal) {
      return;
    }

    setEmail(userSession.email);
    form.setFieldsValue({
      email: userSession.email,
    });
  }, [userSession, showCreateAuthenticatorEmailModal]);

  const onCloseModal = () => {
    dispatch(mfaSlice.setShowAuthenticatorEmailLabel(false));
    dispatch(mfaSlice.setShowCreateAuthenticatorEmailModal(false));
    
    setEmail('');
    setCode('');

    form.resetFields();
    resetErrorFields();
  };

  const renderEmail = () => {
    const hasError = errorFields.includes('email');
    return (
      <>
        <Form.Item
          name='email'
          rules={[{ required: true, pattern: PATTERN.EMAIL, message: t('users.emailRequired') }]}
        >
          <LrvInputV2
            theme={theme}
            onChange={(e) => setEmail(e.target.value)}
            label={t('mfa.email')}
            hasError={hasError}
            value={email}
          />
        </Form.Item>
      </>
    );
  };

  const renderCode = () => {
    const hasError = errorFields.includes('code');

    return (
      <>
        <Form.Item
          name='code'
          rules={[{ required: true, message: t('common.requiredField') }]}
        >
          <Row justify='space-between' align='middle'>
            <Col span={12}>
              <LrvInputV2
                theme={theme}
                onChange={(e) => setCode(e.target.value)}
                label={t('mfa.code')}
                hasError={hasError}
                value={code}
              />
            </Col>

            <ActionButton
              type='primary'
              disabled={!checkEmail(email)}
              loading={isSendingEmail}
              onClick={() => {
                dispatch(mfaSlice.sendMfaEmailCode({ email }));
              }}
            >
              Obtener codigo
            </ActionButton>
          </Row>

        </Form.Item>
      </>
    );
  };

  return (
    <LrvModal
      theme={theme}
      title={t('mfa.title')}
      open={showCreateAuthenticatorEmailModal}
      destroyOnClose={true}
      className={styles.container}
      closeIcon={<Icon id='close_new_user_modal' name='close' />}
      cancelText={t('actions.cancel')}
      onCancel={onCloseModal}
      okText={t('actions.save')}
      okButtonProps={{ loading: isUpdating, disabled: !checkEmail(email) || !code }}
      onOk={() => {
        dispatch(mfaSlice.updateAuthenticatorEmail({ code, onSuccess: onCloseModal }));
      }}
    >
      <LrvForm
        id='form'
        form={form}
        name='form'
        layout='vertical'
        theme={theme}
        onFieldsChange={extractAndSetErrorFields}
      >
        {renderEmail()}
        {renderCode()}

        {
          showAuthenticatorEmailLabel &&
          <Form.Item>
            <LrvText text='Ingresa el codigo enviado al correo electronico' theme={theme}/>
          </Form.Item>
        }
      </LrvForm>
    </LrvModal>
  );
};
